// Backend
import React from 'react';
import axios from '../../helpers/AxiosInstance';

// Frontend
import { Grid, Cell } from 'react-foundation';

// Functionality
import { Link, useNavigate } from 'react-router-dom';

// Components
import {
  Header,
  DluchApi,
  FlashMessage,
  Flashable,
  ProgressBar,
  disableFormButton,
  enableFormButton,
  googleReCaptchaSiteKey
} from '../../constants/SharedComponents';

import ReCAPTCHA from 'react-google-recaptcha';

const RegistrationsNew = () => {
  // API
  const redirectTo = '/registrations';
  const navigate = useNavigate();

  // Objects
  const [loading, setLoading] = React.useState(false);
  const [flashMessageObject, setFlashMessageObject] = React.useState({});
  const [inputs, setInputs] = React.useState({});
  const [capVal, setCapVal] = React.useState(null);
  const basicFormButtons = React.useRef([]);

  const googleSiteKey = googleReCaptchaSiteKey();

  // Functions
  function createSubmission(formInputs) {
    if (formInputs.agree_to_terms_and_privacy_policy !== '1') {
      setFlashMessageObject(
        Flashable('Please confirm acceptance of terms and conditions', 'alert', false),
      );
      enableFormButton(basicFormButtons);
      setLoading(false);
    } else {
      setFlashMessageObject({});

      let requestPayload = {
        firstname: formInputs.forename,
        lastname: formInputs.surname,
        email: formInputs.email_address,
        jobtitle: formInputs.job_title,
        department: formInputs.department
      };

      const Api = new DluchApi('RegistrationsNew');

      axios
        .post(Api.ApiRequestUrl(), requestPayload, { timeout: 30000 })
        .then(() => {
          setFlashMessageObject(Flashable('Please wait...', 'notice', false));
          navigate(redirectTo);
        })
        .catch((error) => {
          if (error.response?.data) {
            setFlashMessageObject(Flashable(error.response.data.errorDesc, 'alert', false));
          } else {
            setFlashMessageObject(Flashable(error.message, 'alert', false));
          }

          enableFormButton(basicFormButtons);
          setLoading(false);
        });
    }
  }

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    disableFormButton(basicFormButtons);
    setLoading(true);
    disableFormButton(basicFormButtons);
    createSubmission(inputs);
  };

  // Content
  return (
    <>
      <ProgressBar loading={loading} />

      <Header />

      <div className="grid-container">
        <Grid className="grid-padding-x align-justify">
          <Cell small={12} large={6}>
            <div className="page-content">
              <h1 className="page-content__title page-content__title--with-subtext">
                Welcome to Forward Together
                <span className="page-content__title-subtext">
                  , funded by DLUHC to change the way workplace well being is managed.
                </span>
              </h1>

              <div className="page-content__styles">
                <p>
                  Using this platform will enable you to have proactive, highly confidential
                  conversations with team members and line managers about mental well being.
                </p>

                <p>Funded by DLUHC Local Digital Fund.</p>
              </div>
            </div>
          </Cell>

          <Cell small={12} large={5}>
            <div className="basic-form basic-form--background">
              <h2 className="basic-form__title">Sign Up and Start the Conversation</h2>

              <div className="basic-form__styles">
                <p>We just need a few more details from you to get started...</p>
              </div>

              <FlashMessage object={flashMessageObject} columnWrap={false} />

              <form className="basic-form" onSubmit={handleSubmit}>
                <div className="basic-form__field">
                  <label
                    htmlFor="forename"
                    className="basic-form__label  basic-form__label--sr-only"
                  >
                    First name:
                  </label>

                  <input
                    className="basic-form__input"
                    type="text"
                    name="forename"
                    id="forename"
                    data-testid="forename"
                    value={inputs.forename || ''}
                    onChange={handleChange}
                    required={true}
                    placeholder="*First name"
                  />
                </div>

                <div className="basic-form__field">
                  <label
                    htmlFor="surname"
                    className="basic-form__label  basic-form__label--sr-only"
                  >
                    Last name:
                  </label>

                  <input
                    className="basic-form__input"
                    type="text"
                    name="surname"
                    id="surname"
                    data-testid="surname"
                    value={inputs.surname || ''}
                    onChange={handleChange}
                    required={true}
                    placeholder="*Last name"
                  />
                </div>

                <div className="basic-form__field">
                  <label
                    htmlFor="email_address"
                    className="basic-form__label  basic-form__label--sr-only"
                  >
                    Email address:
                  </label>

                  <input
                    className="basic-form__input"
                    type="text"
                    name="email_address"
                    id="email_address"
                    data-testid="email_address"
                    value={inputs.email_address || ''}
                    onChange={handleChange}
                    required={true}
                    placeholder="*Email address (example@email.com)"
                  />
                </div>

                <div className="basic-form__field">
                  <label
                    htmlFor="job_title"
                    className="basic-form__label  basic-form__label--sr-only"
                  >
                    Job Title (Optional):
                  </label>

                  <input
                    className="basic-form__input"
                    type="text"
                    name="job_title"
                    id="job_title"
                    data-testid="job_title"
                    value={inputs.job_title || ''}
                    onChange={handleChange}
                    required={false}
                    placeholder="Job Title (Optional)"
                  />
                </div>

                <div className="basic-form__field">
                  <label
                    htmlFor="department"
                    className="basic-form__label  basic-form__label--sr-only"
                  >
                    Department:
                  </label>

                  <input
                    className="basic-form__input"
                    type="text"
                    name="department"
                    id="department"
                    data-testid="department"
                    value={inputs.department || ''}
                    onChange={handleChange}
                    required={false}
                    placeholder="Department (Optional)"
                  />
                </div>

                <div className="basic-form__field">
                  <Grid>
                    <Cell large={1}>
                      <input
                        className="basic-form__input"
                        type="checkbox"
                        name="agree_to_terms_and_privacy_policy"
                        id="agree_to_terms_and_privacy_policy"
                        data-testid="agree_to_terms_and_privacy_policy"
                        value={inputs.agree_to_terms_and_privacy_policy || '1'}
                        onChange={handleChange}
                      />
                    </Cell>
                    <Cell large={11}>
                      <label htmlFor="agree_to_terms_and_privacy_policy" className="basic-form__label">
                        By ticking this box and signing up, you are also agreeing to the{' '}
                        <Link
                          to={process.env.PUBLIC_URL + '/terms-and-conditions.pdf'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="basic-form__link"
                        >
                          Terms and Conditions
                        </Link>{' '}
                        and{' '}
                        <Link
                          to={process.env.PUBLIC_URL + '/privacy-policy.pdf'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="basic-form__link"
                        >Privacy Policy
                        </Link>{' '}
                        of the platform.
                      </label>
                    </Cell>
                  </Grid>
                </div>

                <ReCAPTCHA
                  sitekey={googleSiteKey}
                  onChange={(val) => setCapVal(val)}
                  style={{ paddingBottom: 6 }}
                />

                <div className="basic-form__actions">
                  <input
                    disabled={!capVal}
                    value="Sign-Up"
                    type="submit"
                    className="basic-form__button"
                    data-testid="registration-form-submit"
                    id="page-form-button--submit"
                    ref={(ref) => (basicFormButtons.current[0] = ref)}
                  />
                </div>
              </form>
            </div>
          </Cell>
        </Grid>
      </div>
    </>
  );
};

export default RegistrationsNew;
